import * as v2 from '@/core/services/api/v2';

/**
 * A tela de negócio (armazém + funcionários) falando com o backend V2.
 *
 * `v2.negocio` devolve tudo já no shape que esta store consome — a tradução de
 * forma mora em `services/api/v2`, não aqui.
 */

/**
 * Mensagem de erro no padrão do V2: o backend manda `message` (ou um objeto
 * `error` aninhado); só em último caso caímos no `message` genérico do axios.
 */
function mensagemDeErro(error) {
  return (
    error.response?.data?.message ||
    error.response?.data?.error?.message ||
    error.message
  );
}

const state = {
  storageInfo: null,
  businessData: null,
  hasStorage: false,
  employees: [],
  maxEmployees: 1,
  maxStorageCapacity: 100,
  /** O próximo tier do armazém: mais capacidade E mais alojamento. */
  storageUpgrade: null,
  storedFishCount: 0,
  fishingResults: null,
  isProcessingFishing: false,
  dailyFishingStatus: {} // Quais funcionários já pescaram hoje
};

const mutations = {
  SET_BUSINESS_DATA(state, data) {
    state.businessData = data;
    state.hasStorage = data.hasStorage;

    // Funcionários e limite populam SEMPRE, mesmo sem armazém: no V2 a rota
    // devolve o negócio inteiro numa resposta só, e condicionar tudo ao
    // `hasStorage` deixava a lista vazia para quem ainda não comprou.
    state.employees = data.employees?.employees || [];
    state.maxEmployees = data.employees?.maxCount || 1;
    state.storedFishCount = calculateTotalFishCount(data.inventory?.totalFishByRarity || {});

    // Só o que depende do armazém fica condicionado a ele existir.
    if (state.hasStorage) {
      state.storageInfo = data.storage;
      state.maxStorageCapacity = state.storageInfo?.maxCapacity || 100;
    } else {
      state.storageInfo = null;
    }

    // O degrau seguinte vem do servidor (`TIERS_DO_ARMAZEM`, no domain): é o
    // que a tela mostra no botão de melhorar. Nulo no topo.
    state.storageUpgrade = data.storageUpgrade || null;
  },
  SET_FISHING_RESULTS(state, results) {
    state.fishingResults = results;
  },
  SET_IS_PROCESSING_FISHING(state, status) {
    state.isProcessingFishing = status;
  },
  UPDATE_EMPLOYEE_FISHING_STATUS(state, { employeeId, hasAlreadyFished }) {
    state.dailyFishingStatus = {
      ...state.dailyFishingStatus,
      [employeeId]: hasAlreadyFished
    };

    // Reflete também na lista de funcionários
    const employeeIndex = state.employees.findIndex(e => e.id === employeeId);
    if (employeeIndex !== -1) {
      state.employees[employeeIndex] = {
        ...state.employees[employeeIndex],
        hasAlreadyFished
      };
    }
  },
  UPDATE_STORAGE_FISH_COUNT(state, newCount) {
    state.storedFishCount = newCount;
  }
};

const actions = {
  async loadBusinessData({ commit }, playerId) {
    try {
      // O V2 devolve armazém, funcionários e estoque numa resposta só, já no
      // shape que `SET_BUSINESS_DATA` espera.
      const result = await v2.negocio(playerId);
      commit('SET_BUSINESS_DATA', result);

      // Inicializa o status de pesca diária de cada funcionário
      if (result.employees && result.employees.employees) {
        result.employees.employees.forEach(employee => {
          commit('UPDATE_EMPLOYEE_FISHING_STATUS', {
            employeeId: employee.id,
            hasAlreadyFished: employee.hasAlreadyFished === true // Força booleano
          });
        });
      }

      return result;
    } catch (error) {
      console.error('Error loading business data:', error);
      throw new Error(mensagemDeErro(error));
    }
  },

  async processDailyFishing({ commit, dispatch, rootState }, employeeId) {
    try {
      commit('SET_IS_PROCESSING_FISHING', true);

      const playerId = rootState.player?.id;
      // O servidor valida mapa/vara/isca e devolve 409 com mensagem clara se
      // faltar algo ou se o funcionário já pescou hoje.
      const result = await v2.pescaDiaria(playerId, employeeId);

      if (result.success) {
        commit('SET_FISHING_RESULTS', result);

        commit('UPDATE_EMPLOYEE_FISHING_STATUS', {
          employeeId,
          hasAlreadyFished: true
        });

        // Os peixes vão para o armazém da EMPRESA (desde 25/09/2026), não para
        // o inventário do jogador: basta recarregar o negócio.
        await dispatch('loadBusinessData', playerId);
      }

      return result;
    } catch (error) {
      console.error('Error processing daily fishing:', error);
      throw new Error(mensagemDeErro(error));
    } finally {
      commit('SET_IS_PROCESSING_FISHING', false);
    }
  },

  /**
   * Compra do armazém. O preço (5.000) é cobrado NO SERVIDOR — nada de débito
   * local de gold; o servidor devolve o saldo final. 409 se já tem armazém.
   */
  async purchaseStorage({ commit, dispatch }, playerId) {
    try {
      const resultado = await v2.comprarArmazem(playerId);

      // O gold que vale é o que o servidor devolveu
      commit('player/SET_GOLD', resultado.gold, { root: true });

      // Recarrega o negócio para o dashboard refletir o armazém novo
      await dispatch('loadBusinessData', playerId);

      // Shape que o StoragePurchaseModal espera no `success`
      return {
        success: true,
        data: {
          remainingGold: resultado.gold,
          storage: { maxCapacity: resultado.capacity }
        }
      };
    } catch (error) {
      console.error('Error purchasing storage:', error);
      throw new Error(mensagemDeErro(error));
    }
  },

  /**
   * Melhora o armazém um tier. Sobe capacidade **e** alojamento
   * (`max_employees`) — é o que destrava contratar o segundo funcionário. O
   * custo e os requisitos são do servidor; o gold vem de volta na resposta.
   */
  async upgradeStorage({ commit, dispatch }, playerId) {
    try {
      const resultado = await v2.melhorarArmazem(playerId);
      commit('player/SET_GOLD', resultado.gold, { root: true });
      await dispatch('loadBusinessData', playerId);
      return {
        success: true,
        data: {
          tier: resultado.tier,
          remainingGold: resultado.gold,
          storage: { maxCapacity: resultado.capacity },
          maxEmployees: resultado.maxEmployees
        }
      };
    } catch (error) {
      console.error('Error upgrading storage:', error);
      throw new Error(mensagemDeErro(error));
    }
  },

  /**
   * Demite. O servidor devolve a isca que estava com o funcionário ao estoque
   * do jogador; a vara era emprestada e não sai de lugar nenhum.
   */
  async demitir({ dispatch }, { playerId, employeeId }) {
    try {
      await v2.demitir(playerId, employeeId);
      await dispatch('loadBusinessData', playerId);
    } catch (error) {
      console.error('Error dismissing employee:', error);
      throw new Error(mensagemDeErro(error));
    }
  },

  /**
   * Paga a dívida da folha (0060). Recarrega jogador e negócio do servidor: o
   * ouro e a dívida que valem são os dele.
   */
  async pagarDivida({ dispatch }, playerId) {
    try {
      const resultado = await v2.pagarDividaDaFolha(playerId);
      await dispatch('player/loadPlayerState', playerId, { root: true });
      await dispatch('loadBusinessData', playerId);
      return resultado;
    } catch (error) {
      console.error('Error paying payroll debt:', error);
      throw new Error(mensagemDeErro(error));
    }
  },

  resetFishingResults({ commit }) {
    commit('SET_FISHING_RESULTS', null);
  }
};

const getters = {
  getBusinessData: state => state.businessData,
  hasStorage: state => state.hasStorage,
  getEmployees: state => state.employees,
  getMaxEmployees: state => state.maxEmployees,
  getMaxStorageCapacity: state => state.maxStorageCapacity,
  getStorageUpgrade: state => state.storageUpgrade,
  getStoredFishCount: state => state.storedFishCount,
  getFishingResults: state => state.fishingResults,
  isProcessingFishing: state => state.isProcessingFishing,
  hasEmployeeFishedToday: state => employeeId => {
    return state.dailyFishingStatus[employeeId] || false;
  }
};

// Soma total de peixes por raridade
function calculateTotalFishCount(totalFishByRarity) {
  return Object.values(totalFishByRarity).reduce((sum, count) => sum + count, 0);
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
